import { useEffect, useState } from "react";
import { api, formatRupiah, dispDate, todayYMD } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Wallet, TrendingUp, CheckCircle2 } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

const emptyForm = { user_id: "", amount: "", paid_date: todayYMD(), note: "" };

export default function AdminPhlPayouts() {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailUser, setDetailUser] = useState(null); // user summary row when opened
  const [detailRows, setDetailRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/phl/summary");
      setSummary(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const openDetail = async (row) => {
    setDetailUser(row);
    const r = await api.get("/phl/payouts", { params: { user_id: row.user_id } });
    setDetailRows(r.data);
  };

  const closeDetail = () => { setDetailUser(null); setDetailRows([]); };

  const startCreate = (userRow) => {
    setEditing(null);
    setForm({ ...emptyForm, user_id: userRow.user_id });
    setOpenForm(true);
  };

  const startEdit = (payout) => {
    setEditing(payout);
    setForm({
      user_id: payout.user_id,
      amount: String(payout.amount),
      paid_date: payout.paid_date,
      note: payout.note || "",
    });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.amount || !form.paid_date) {
      toast.error("Lengkapi field wajib");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/phl/payouts/${editing.id}`, {
          amount: parseFloat(form.amount),
          paid_date: form.paid_date,
          note: form.note.trim(),
        });
        toast.success("Pembayaran diperbarui");
      } else {
        await api.post("/phl/payouts", {
          user_id: form.user_id,
          amount: parseFloat(form.amount),
          paid_date: form.paid_date,
          note: form.note.trim(),
        });
        toast.success("Pembayaran dicatat");
      }
      setOpenForm(false);
      await refresh();
      if (detailUser) {
        const r = await api.get("/phl/payouts", { params: { user_id: detailUser.user_id } });
        setDetailRows(r.data);
        // Refresh detail user summary
        const fresh = summary.find((s) => s.user_id === detailUser.user_id);
        if (fresh) setDetailUser(fresh);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const del = async (payout) => {
    if (!window.confirm(`Hapus pembayaran ${formatRupiah(payout.amount)} tanggal ${dispDate(payout.paid_date)}?`)) return;
    try {
      await api.delete(`/phl/payouts/${payout.id}`);
      toast.success("Pembayaran dihapus");
      await refresh();
      if (detailUser) {
        const r = await api.get("/phl/payouts", { params: { user_id: detailUser.user_id } });
        setDetailRows(r.data);
      }
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const totalEarned = summary.reduce((s, r) => s + (r.total_earned || 0), 0);
  const totalPaid = summary.reduce((s, r) => s + (r.total_paid || 0), 0);
  const totalBalance = summary.reduce((s, r) => s + (r.balance || 0), 0);

  if (loading) return <PageLoader label="Memuat saldo PHL..." />;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto" data-testid="admin-phl-payouts-page">
      <p className="overline">HRD</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Saldo Upah PHL</h1>
      <p className="text-sm text-zinc-500 mt-1">
        Pegawai PHL upahnya terkumpul harian. Admin catat setiap kali pegawai mengambil upahnya (bisa sebagian atau penuh).
      </p>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <Card className="p-4 border border-zinc-200 shadow-none">
          <div className="flex items-center gap-2 text-zinc-500">
            <TrendingUp className="w-4 h-4" />
            <p className="overline text-[0.6rem]">Total Terkumpul</p>
          </div>
          <p className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-1" data-testid="phl-total-earned">
            {formatRupiah(totalEarned)}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-4 h-4" />
            <p className="overline text-[0.6rem]">Sudah Dibayar</p>
          </div>
          <p className="font-display text-xl sm:text-2xl font-bold tracking-tight text-emerald-700 mt-1" data-testid="phl-total-paid">
            {formatRupiah(totalPaid)}
          </p>
        </Card>
        <Card className="p-4 border border-amber-300 bg-amber-50 shadow-none">
          <div className="flex items-center gap-2 text-amber-800">
            <Wallet className="w-4 h-4" />
            <p className="overline text-[0.6rem]">Belum Dibayar</p>
          </div>
          <p className="font-display text-xl sm:text-2xl font-bold tracking-tight text-amber-800 mt-1" data-testid="phl-total-balance">
            {formatRupiah(totalBalance)}
          </p>
        </Card>
      </div>

      {/* List */}
      <div className="mt-6 space-y-3">
        {summary.length === 0 && (
          <Card className="p-12 text-center border border-zinc-200 shadow-none">
            <p className="text-sm text-zinc-500">Belum ada pegawai PHL.</p>
          </Card>
        )}
        {summary.map((row) => (
          <Card
            key={row.user_id}
            className={`p-4 border shadow-none ${row.balance > 0 ? "border-amber-300" : "border-zinc-200"}`}
            data-testid={`phl-row-${row.employee_id}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-semibold">{row.name}</p>
                <p className="text-xs text-zinc-500 font-mono">{row.employee_id} · Rp {row.hourly_rate.toLocaleString("id-ID")}/jam</p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-6 text-right">
                <div>
                  <p className="text-[0.6rem] uppercase text-zinc-500 tracking-widest">Terkumpul</p>
                  <p className="font-semibold text-sm">{formatRupiah(row.total_earned)}</p>
                </div>
                <div>
                  <p className="text-[0.6rem] uppercase text-emerald-700 tracking-widest">Dibayar</p>
                  <p className="font-semibold text-sm text-emerald-700">{formatRupiah(row.total_paid)}</p>
                </div>
                <div>
                  <p className="text-[0.6rem] uppercase text-amber-800 tracking-widest">Sisa</p>
                  <p className="font-semibold text-sm text-amber-800" data-testid={`phl-balance-${row.employee_id}`}>
                    {formatRupiah(row.balance)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  onClick={() => startCreate(row)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`btn-pay-${row.employee_id}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Catat Bayar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openDetail(row)}
                  data-testid={`btn-detail-${row.employee_id}`}
                >
                  Detail
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detailUser} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailUser && (
            <>
              <DialogHeader>
                <DialogTitle>{detailUser.name}</DialogTitle>
                <DialogDescription>
                  Terkumpul {formatRupiah(detailUser.total_earned)} · Terbayar {formatRupiah(detailUser.total_paid)} ·
                  Sisa <span className="text-amber-800 font-semibold">{formatRupiah(detailUser.balance)}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => { closeDetail(); startCreate(detailUser); }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="btn-add-payout-in-detail"
                >
                  <Plus className="w-4 h-4 mr-1" /> Catat Pembayaran Baru
                </Button>
              </div>

              <div className="overflow-x-auto mt-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tanggal</TableHead>
                      <TableHead className="text-right">Nominal</TableHead>
                      <TableHead>Catatan</TableHead>
                      <TableHead className="w-24">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-zinc-500 py-8">
                          Belum ada pembayaran tercatat
                        </TableCell>
                      </TableRow>
                    )}
                    {detailRows.map((p) => (
                      <TableRow key={p.id} data-testid={`payout-${p.id}`}>
                        <TableCell>{dispDate(p.paid_date)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatRupiah(p.amount)}</TableCell>
                        <TableCell className="text-xs text-zinc-600">{p.note || "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => startEdit(p)} data-testid={`btn-edit-payout-${p.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => del(p)} className="text-red-600" data-testid={`btn-del-payout-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pembayaran" : "Catat Pembayaran"}</DialogTitle>
            <DialogDescription>
              {summary.find((s) => s.user_id === form.user_id)?.name || "Pegawai PHL"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nominal (Rp) *</Label>
              <Input
                type="number"
                step="1"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
                data-testid="input-payout-amount"
              />
            </div>
            <div>
              <Label>Tanggal Bayar *</Label>
              <Input
                type="date"
                value={form.paid_date}
                onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                required
                data-testid="input-payout-date"
              />
            </div>
            <div>
              <Label>Catatan (opsional)</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={2}
                placeholder="misal: penarikan mingguan, bagian dari upah minggu ke-2, dll"
                data-testid="input-payout-note"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)}>Batal</Button>
              <Button
                type="submit"
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="btn-submit-payout"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Update" : "Simpan")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
